import { useEffect, useState } from 'react'
import api from '../api/client'
import Modal from './Modal'
import Boton from './Boton'

const fmt = (n) => `$${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`

/**
 * Modal "Refacturar" (Martin, 16 oct 2026). Pedido ya entregado que necesita
 * otra factura por un motivo contable:
 *   1. el vendedor emite en Presea la DEVOLUCIÓN total (repone el stock) y pega
 *      el número acá; LGR la verifica y deja la venta lista, SIN tocar la
 *      entrega ni el cobro;
 *   2. corrige lo que haga falta y emite la factura nueva desde el detalle
 *      (sale sin logística).
 */
export default function RefacturacionModal({ nvId, onCerrar, onHecho }) {
  const [pre, setPre] = useState(null)
  const [numeros, setNumeros] = useState('')
  const [motivo, setMotivo] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [aviso, setAviso] = useState(null)
  const [hecho, setHecho] = useState(null)

  useEffect(() => {
    api.get(`/notas-venta/${nvId}/refacturacion/prechequeo`)
      .then(({ data }) => setPre(data))
      .catch(() => setAviso('No se pudo cargar el pedido.'))
  }, [nvId])

  const registrar = () => {
    if (!numeros.trim()) return setAviso('Pegá el número de la DEVOLUCIÓN emitida en Presea.')
    if (motivo.trim().length < 5) return setAviso('Escribí el motivo de la refacturación.')
    setEnviando(true)
    setAviso(null)
    api.post(`/notas-venta/${nvId}/refacturacion/devolucion`, { numeros: numeros.trim(), motivo: motivo.trim() })
      .then(({ data }) => { setHecho(data.mensaje); onHecho?.() })
      .catch(e => {
        const errs = e.response?.data?.errors
        setAviso(errs ? Object.values(errs).flat().join(' · ') : (e.response?.data?.message || 'No se pudo registrar.'))
      })
      .finally(() => setEnviando(false))
  }

  return (
    <Modal title="🔁 Refacturar" onClose={onCerrar} maxWidth="600px">
      {!pre && !aviso && <div style={s.dim}>Cargando…</div>}

      {hecho && (
        <div>
          <div style={{ ...s.banner, background: '#f0fdf4', borderColor: '#86efac', color: '#166534' }}>✓ {hecho}</div>
          <div style={{ textAlign: 'right' }}><Boton variante="primary" onClick={onCerrar}>Listo</Boton></div>
        </div>
      )}

      {pre && !hecho && (
        <div>
          {!pre.puede && (
            <div style={{ ...s.banner, background: '#fffbeb', borderColor: '#fcd34d', color: '#92400e' }}>{pre.impedimento}</div>
          )}

          <div style={s.texto}>
            Para cuando el cliente necesita otra factura y el pedido ya se entregó. La entrega no se toca
            y el cobro queda en esta venta.
          </div>

          <div style={s.titulo}>Paso 1 — en Presea</div>
          <div style={{ ...s.banner, background: '#eff6ff', borderColor: '#bfdbfe', color: '#1e40af' }}>
            Emití la <b>DEVOLUCIÓN</b> de la factura <span style={s.mono}>{pre.factura}</span> por el <b>total</b>{' '}
            ({fmt(pre.importe)}, con percepciones){pre.deposito ? <> al depósito <b>{pre.deposito}</b></> : null}.
            Tiene que ser DEVOLUCIÓN (no nota de crédito): es la que devuelve el stock.
          </div>

          <input value={numeros} onChange={e => setNumeros(e.target.value)} disabled={!pre.puede}
            placeholder="Nº de la DEVOLUCIÓN (si salió en varias: separadas por coma)" style={s.input} />
          <input value={motivo} onChange={e => setMotivo(e.target.value)} disabled={!pre.puede}
            placeholder="Motivo (ej.: el cliente necesita factura A a nombre de su empresa)" style={{ ...s.input, fontFamily: 'inherit' }} />

          <div style={s.titulo}>Paso 2 — en LGR</div>
          <div style={s.texto}>
            Al validar, la venta queda en borrador. Corregí lo que haga falta (cliente, condición fiscal, razón social)
            y apretá <b>🧾 Emitir factura nueva</b>. Sale la factura con su remito, sin pasar por logística.
          </div>
          {pre.cobros.length > 0 && (
            <div style={s.texto}>
              El cobro ({pre.cobros.map(c => `${c.numero_presea || '#' + c.id} ${fmt(c.importe)}`).join(', ')}) queda en la venta:
              no se vuelve a mandar. Si la factura nueva da otro importe, la diferencia se cobra o se devuelve aparte.
            </div>
          )}

          {aviso && <div style={{ marginTop: '0.6rem', fontSize: '0.8rem', color: '#b91c1c' }}>✕ {aviso}</div>}

          <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Boton variante="ghost" onClick={onCerrar}>Cancelar</Boton>
            <Boton variante="primary" onClick={registrar} disabled={enviando || !pre.puede}>
              {enviando ? 'Validando contra Presea…' : '✓ Validar devolución'}
            </Boton>
          </div>
        </div>
      )}
      {!pre && aviso && <div style={{ fontSize: '0.85rem', color: '#b91c1c' }}>{aviso}</div>}
    </Modal>
  )
}

const s = {
  banner: { padding: '0.7rem 0.9rem', border: '1px solid', borderRadius: 8, fontSize: '0.82rem', lineHeight: 1.5, marginBottom: '0.6rem' },
  titulo: { fontWeight: 700, fontSize: '0.8rem', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.03em', margin: '0.9rem 0 0.35rem' },
  texto: { fontSize: '0.82rem', color: '#475569', lineHeight: 1.55, marginBottom: '0.4rem' },
  mono: { fontFamily: 'ui-monospace, monospace' },
  dim: { fontSize: '0.8rem', color: '#94a3b8' },
  input: { width: '100%', boxSizing: 'border-box', marginTop: '0.6rem', padding: '0.5rem 0.7rem', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: '0.85rem', fontFamily: 'ui-monospace, monospace' },
}
